import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fetchAdStatusAlerts, markNotificationsRead } from "@/lib/api";
import { CheckCircle2, XCircle, Clock, ChevronRight, PauseCircle, Star, CalendarClock, MapPinned, MapPin, BarChart3, ChevronDown } from "lucide-react";
import { RestockRow, WeeklySummaryRow, AdExpiryRow } from "@/components/vendor/VendorBellRows";
import { expiryLabel } from "@/components/Cards";

const ICON = {
  approved: { Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50", label: "Approved" },
  rejected: { Icon: XCircle, cls: "text-rose-700 bg-rose-50", label: "Rejected" },
  pending: { Icon: Clock, cls: "text-amber-700 bg-amber-50", label: "Pending approval" },
  paused: { Icon: PauseCircle, cls: "text-sky-700 bg-sky-50", label: "Paused" },
  expired: { Icon: CheckCircle2, cls: "text-slate-500 bg-slate-100", label: "Completed" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function AlertRow({ item, to, unread }) {
  const m = ICON[item.kind];
  return (
    <Link to={to} className="flex items-start gap-3 bg-white border border-slate-100 hover:border-emerald-200 rounded-xl p-3 transition-colors" data-testid={`ad-alert-${item.key}`}>
      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.cls}`}><m.Icon className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-1">
          {unread && <span className="me-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={`ad-alert-unread-${item.key}`} />}
          {item.title || item.ad_id}
        </div>
        <div className="text-[11px] text-slate-500 line-clamp-1">
          <b className={m.cls.split(" ")[0]}>{m.label}{item.paused_by === "vendor" ? " (by you)" : item.paused_by === "admin" ? " (by admin)" : ""}</b> · {fmt(item.at)}{item.vendor_name || item.vendor_email ? ` · ${item.vendor_name || item.vendor_email}` : ""} · PIN {(item.pincodes || []).join(", ")} · {item.ad_id}
        </div>
        {item.note && <div className="text-xs text-rose-700 mt-0.5 line-clamp-2" data-testid={`ad-alert-note-${item.key}`}>Reason: {item.note}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 flex-shrink-0" />
    </Link>
  );
}

function ReviewRow({ item, unread }) {
  return (
    <Link to={item.link} className="flex items-start gap-3 bg-white border border-slate-100 hover:border-emerald-200 rounded-xl p-3 transition-colors" data-testid={`review-alert-${item.key}`}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-amber-700 bg-amber-50"><Star className="w-4 h-4 fill-current" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-1">
          {unread && <span className="me-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={`review-alert-unread-${item.key}`} />}
          <span className="text-amber-600">{"★".repeat(item.rating || 0)}</span> {item.customer} {item.edited ? "ne review edit kiya" : "ne review diya"}
        </div>
        <div className="text-[11px] text-slate-500 line-clamp-1">{item.target} · {fmt(item.at)}</div>
        {item.comment && <div className="text-xs text-slate-700 mt-0.5 line-clamp-1">"{item.comment}"</div>}
        <span className="inline-block mt-1 text-xs font-semibold text-emerald-700" data-testid={`review-alert-reply-${item.key}`}>Reply karein →</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 flex-shrink-0" />
    </Link>
  );
}

function JobExpiryRow({ item, unread }) {
  return (
    <Link to={item.link} className="flex items-start gap-3 bg-white border border-amber-200 hover:border-emerald-300 rounded-xl p-3 transition-colors" data-testid={`job-expiry-alert-${item.job_id}`}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-amber-700 bg-amber-50"><CalendarClock className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-2">
          {unread && <span className="me-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={`job-expiry-alert-unread-${item.job_id}`} />}
          Vacancy "{item.title}" expire hone wali hai
        </div>
        <div className="text-[11px] text-slate-500"><b className="text-amber-700" data-testid={`job-expiry-alert-label-${item.job_id}`}>{expiryLabel(item) || "Expire hone wali hai"}</b> · Last date {item.last_date} {item.last_time} IST · PIN {item.pincode}</div>
        <span className="inline-block mt-1 text-xs font-semibold text-emerald-700" data-testid={`job-expiry-alert-extend-${item.job_id}`}>Last date badha kar extend karein →</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 flex-shrink-0" />
    </Link>
  );
}

function LocationRow({ item, unread }) {
  const text = item.kind === "location_expiring" ? `Aapki shared location 1 ghante se kam mein expire hogi${item.who ? ` (${item.who})` : ""}` : item.kind === "location_reply" ? `${item.who} ne aapko apni location bheji` : `${item.who} ne aapke saath location share ki`;
  return (
    <Link to={item.link} className="flex items-start gap-3 bg-white border border-emerald-200 hover:border-emerald-400 rounded-xl p-3 transition-colors" data-testid={`location-alert-${item.key}`}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-emerald-700 bg-emerald-50"><MapPinned className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-2">
          {unread && <span className="me-1.5 inline-block w-2 h-2 rounded-full bg-rose-600 align-middle" data-testid={`location-alert-unread-${item.key}`} />}
          {text}
        </div>
        <div className="text-[11px] text-slate-500">{item.service_name ? `${item.service_name} · ` : ""}{fmt(item.expires_at)} tak valid (24 ghante)</div>
        <span className="inline-block mt-1 text-xs font-semibold text-emerald-700" data-testid={`location-alert-open-${item.key}`}>{item.kind === "location_expiring" ? "Dekhein ya dobara share karein →" : item.kind === "location_reply" ? "Directions dekhein →" : "Customer ki location dekhein →"}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 mt-2 flex-shrink-0" />
    </Link>
  );
}

function Section({ title, items, to, unreadKeys, testid }) {
  if (!items.length) return null;
  return (
    <div className="mb-5" data-testid={testid}>
      <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</h2>
      <div className="space-y-2">
        {items.map((i) => <AlertRow key={i.key} item={i} to={to} unread={unreadKeys.has(i.key)} />)}
      </div>
    </div>
  );
}

// Two compact toggles; collapsed by default, one open at a time, content opens below the row.
function NotifToggles({ locations, weekly, unreadKeys }) {
  const [open, setOpen] = useState("");
  const tabs = [["location", "Location", MapPin, locations], ["weekly", "Weekly Shop Summary", BarChart3, weekly]];
  const cur = tabs.find((t) => t[0] === open);
  return (
    <div className="mb-5" data-testid="notif-toggles">
      <div className="grid grid-cols-2 gap-2">
        {tabs.map(([k, label, Icon, items]) => {
          const on = open === k;
          const unread = items.filter((i) => unreadKeys.has(i.key)).length;
          return (
            <button key={k} type="button" onClick={() => setOpen(on ? "" : k)} aria-expanded={on}
              className={`h-12 min-w-0 inline-flex items-center justify-center gap-1.5 rounded-xl border px-2 text-[12px] min-[400px]:text-[13px] font-semibold transition-colors ${on ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-emerald-200 text-emerald-900 hover:bg-emerald-50"}`} data-testid={`notif-toggle-${k}`}>
              <Icon className="w-4 h-4 flex-shrink-0" /><span className="leading-tight text-center min-w-0">{label}</span>
              {items.length > 0 && <span className={`flex-shrink-0 rounded-full px-1.5 text-[11px] leading-5 ${on ? "bg-white/20" : unread ? "bg-amber-500 text-white" : "bg-emerald-100"}`} data-testid={`notif-toggle-count-${k}`}>{items.length}</span>}
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${on ? "rotate-180" : ""}`} />
            </button>
          );
        })}
      </div>
      {cur && (
        <div className="mt-3 space-y-2" data-testid={cur[0] === "location" ? "location-alerts" : "weekly-summary-alerts"}>
          {!cur[3].length
            ? <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-xs text-slate-500" data-testid={`notif-empty-${cur[0]}`}>{cur[0] === "location" ? "Abhi koi shared location nahi hai." : "Abhi koi weekly shop summary nahi hai."}</div>
            : cur[3].map((i) => (cur[0] === "location" ? <LocationRow key={i.key} item={i} unread={unreadKeys.has(i.key)} /> : <WeeklySummaryRow key={i.key} item={i} unread={unreadKeys.has(i.key)} />))}
        </div>
      )}
    </div>
  );
}

export default function AdStatusAlerts({ refreshKey = 0 }) {
  const { user, refreshUnread } = useApp();
  const [data, setData] = useState({ vendor: [], admin: [], reviews: [], jobs: [], restock: [], weekly: [], locations: [], ad_expiry: [] });
  const [unreadKeys, setUnreadKeys] = useState(new Set());
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const on = () => setPulse((n) => n + 1);
    window.addEventListener("vb:location-pulse", on);
    return () => window.removeEventListener("vb:location-pulse", on);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    fetchAdStatusAlerts().then(async (d) => {
      if (cancelled) return;
      setData({ vendor: [], admin: [], reviews: [], jobs: [], restock: [], weekly: [], locations: [], ...(d || {}) });
      const fresh = [...(d?.vendor || []), ...(d?.admin || []), ...(d?.reviews || []), ...(d?.jobs || []), ...(d?.restock || []), ...(d?.weekly || []), ...(d?.locations || []), ...(d?.ad_expiry || [])].filter((i) => !i.read).map((i) => i.key);
      setUnreadKeys(new Set(fresh));
      if (fresh.length) { await markNotificationsRead(fresh).catch(() => {}); refreshUnread(); }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user?.id, refreshKey, pulse]);

  return (
    <>
      <NotifToggles locations={data.locations || []} weekly={data.weekly || []} unreadKeys={unreadKeys} />
      <Section title="Admin: Pending Ads" items={data.admin} to="/admin?tab=ads" unreadKeys={unreadKeys} testid="admin-pending-ads-alerts" />
      {data.jobs?.length > 0 && (
        <div className="mb-5" data-testid="job-expiry-alerts">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Vacancy Expiry Reminder</h2>
          <div className="space-y-2">{data.jobs.map((i) => <JobExpiryRow key={i.key} item={i} unread={unreadKeys.has(i.key)} />)}</div>
        </div>
      )}
      {data.restock?.length > 0 && (
        <div className="mb-5" data-testid="restock-alerts">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Restock Reminder</h2>
          <div className="space-y-2">{data.restock.map((i) => <RestockRow key={i.key} item={i} unread={unreadKeys.has(i.key)} onChanged={() => { setPulse((n) => n + 1); refreshUnread(); }} />)}</div>
        </div>
      )}
      {data.reviews?.length > 0 && (
        <div className="mb-5" data-testid="review-alerts">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Naye Reviews</h2>
          <div className="space-y-2">{data.reviews.map((i) => <ReviewRow key={i.key} item={i} unread={unreadKeys.has(i.key)} />)}</div>
        </div>
      )}
      {data.ad_expiry?.length > 0 && (
        <div className="mb-5" data-testid="ad-expiry-alerts">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Ad Expiry Reminder</h2>
          <div className="space-y-2">{data.ad_expiry.map((i) => <AdExpiryRow key={i.key} item={i} unread={unreadKeys.has(i.key)} />)}</div>
        </div>
      )}
      <Section title="Aapke Ads" items={data.vendor} to="/vendor?tab=ads" unreadKeys={unreadKeys} testid="vendor-ad-status-alerts" />
    </>
  );
}
